import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import { Alert, Box, Button, Card, Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme } from "../../../Theme/theme";
import { GET } from "../../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import SearchableInput from "../../Dashboard/Components/SearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";

/* ================= TYPES ================= */

type Filter = {
    search: string;
    district: number[] | "all";
    village: number[] | "all";
    powerstation: number[];
};

const initialFilter: Filter = {
    search: "",
    district: [],
    village: [],
    powerstation: [],
};

/* ================= API ================= */

const fetchHamlets = (params: any = {}) => {
    return GET("/region/hamlet", params);
};

/* ================= COMPONENT ================= */

const Hamlet = () => {
    const [feedback, setFeedback] = useState("");
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, limit: 100 });
    const [changeCount, setChangeCount] = useState(0);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const [filter, setFilter] = useState<any>(initialFilter);

    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        powerstation: false,
        search: false,
    });

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            search: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    search: false,
                }),
            0
        );
    };

    /* ================= QUERY ================= */

    const { data, isLoading, isSuccess, isError } = useQuery(
        ["hamlets", pagination.page, pagination.limit, filter],
        () =>
            fetchHamlets({
                page: pagination.page + 1,
                limit: pagination.limit,
                ...Object.fromEntries(
                    Object.entries(filter).filter(([_, v]) =>
                        Array.isArray(v) ? v.length : v && v !== "all"
                    )
                ),
            }),
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    /* ================= APPLY FILTER ================= */

    useEffect(() => {
        if (changeCount) {
            setFilter(intermediateFilter);
        }
    }, [changeCount]);

    /* ================= UI ================= */

    return (
        <Box>
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Typography color="GrayText" variant="h5">
                            Hamlets
                        </Typography>
                    </Grid>

                    <Grid item>
                        <MultiPermissionAuthorize ops={["CREATE HAMLET"]}>
                            <Link
                                to="/regions/hamlets/add"
                                style={{ textDecoration: "none" }}
                            >
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<Add />}
                                    sx={{
                                        height: "100%",
                                        minWidth: theme.spacing(20),
                                    }}
                                    fullWidth
                                >
                                    add hamlet
                                </Button>
                            </Link>
                        </MultiPermissionAuthorize>
                    </Grid>
                </Grid>

                {/* FILTERS */}
                <Grid container spacing={1} mt={1}>
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={4}>
                            <UpdatedSearchableInput
                                label="Districts"
                                _name="district"
                                api="/region/district"
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clearAll={clearAll.district}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={4}>
                            <SearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                dep={{ district: intermediateFilter.district }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={4}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Villages"
                                _name="village"
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                dep={[
                                    intermediateFilter.district,
                                    intermediateFilter.powerstation,
                                ]}
                                params={{
                                    district: intermediateFilter.district
                                        ? intermediateFilter.district
                                        : 0,
                                    powerstation:
                                        intermediateFilter.powerstation
                                            ? intermediateFilter.powerstation
                                            : 0,
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={4}>
                        <SearchBar
                            filter={intermediateFilter}
                            setFilter={setIntermediateFilter}
                            clear={clearAll.search}
                        />
                    </Grid>

                    {/* SEARCH */}
                    <Grid item xs={12} md={4}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            startIcon={<FilterAltOutlined />}
                            onClick={() => setChangeCount((prev) => prev + 1)}
                            sx={{
                                height: "100%",
                                minWidth: theme.spacing(20),
                            }}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    {/* CLEAR ALL */}
                    <Grid item xs={12} md={4}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{
                                height: "100%",
                                minWidth: theme.spacing(20),
                            }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* INFO */}
            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    {!data?.data.rows.length && isSuccess
                        ? noDataMessage
                        : !hasValue(filter) && !changeCount
                        ? filterSelectionMessage
                        : filterSelectionMessage}
                </Alert>
            )}

            {/* TABLE */}
            <BaseTable
                headers={{
                    id: "ID",
                    name: "Name",
                    districtName: "District",
                    villageName: "Village",
                    powerstationName: "Powerstation",
                }}
                data={data?.data.rows.map((row: any) => ({
                    ...row,
                    villageName: row.village.name,
                    districtName: row.village.district.name,
                    powerstationName: row.powerstation.name,
                }))}
                defaultSelectedHeaders={[
                    "id",
                    "name",
                    "districtName",
                    "villageName",
                    "powerstationName",
                ]}
                delEndPoint="/region/hamlet"
                feedback={feedback}
                setFeedback={setFeedback}
                rowsCount={rowsCount}
                pagination={pagination}
                setPagination={setPagination}
                isLoading={isLoading}
                permissions={{
                    edit: ["UPDATE HAMLET"],
                    delete: ["DELETE HAMLET"],
                }}
                queryKey={[
                    "hamlets",
                    pagination.page,
                    pagination.limit,
                    filter,
                ]}
                reportName="Hamlets__List"
                load={Boolean(changeCount)}
                reportDetails={{
                    district: filter.district ? filter.district : undefined,
                    powerstation: filter.powerstation
                        ? filter.powerstation
                        : undefined,
                    village: filter.village ? filter.village : undefined,
                    hamlet: filter.hamlet ? filter.hamlet : undefined,
                }}
            />
        </Box>
    );
};

export default Hamlet;
